import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Assignment,
  convertAssignmentIntoAssignmentPayload,
  useCreateAssignmentMutation,
  useEditAssignmentMutation,
} from 'shared/api/assignment-service';
import {
  eventService,
  generateAssignmentActionsContextEventPayload,
  generateAssignmentActionsResultEventPayload,
  getPage,
  XApiEventKeys,
} from 'shared/api/event-service';
import { NotificationPopup } from 'shared/ui/notification-popup';
import { useAppDispatch, useAppSelector } from 'shared/lib/redux';
import { ASSIGNMENT_UPDATED_EVENT, RealtimeService } from 'shared/lib/realtime';
import { usePrevious } from 'shared/lib/utils';
import { useClassData, useClasses } from 'entities/classes';
import {
  getAssignmentSection,
  isScheduledAssignment,
} from 'entities/assignments';
import { useAssignmentActionTimestampHandlers } from 'features/assignment-panel';

import { setIsAssignmentActionNotificationActive } from '../../model';

import { RevertArrowIcon } from './icons/revert-arrow-icon';
import styles from './styles.module.scss';

export const AssignmentActionNotificationPopup = () => {
  const { t } = useTranslation();

  const [initialEditingAssignment, setInitialEditingAssignment] =
    useState<Assignment | null>(null);

  const dispatch = useAppDispatch();

  const { writeAssignmentActionTimestamp } =
    useAssignmentActionTimestampHandlers();

  const {
    editingAssignment,
    isAssignmentActionNotificationActive,
    isAssignmentPanelOpened,
    assignmentActionErrorMessage,
  } = useAppSelector((state) => state.assignmentPanel);

  const notificationVariant = assignmentActionErrorMessage
    ? 'error'
    : 'success';

  const prevIsAssignmentPanelOpened = usePrevious(isAssignmentPanelOpened);

  const { classes, isFetching } = useClasses();
  const { classData } = useClassData();

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const [editAssignment] = useEditAssignmentMutation();
  const [createAssignment] = useCreateAssignmentMutation();

  const isAssignmentScheduled = useMemo(() => {
    if (!initialEditingAssignment) return false;

    return isScheduledAssignment(initialEditingAssignment);
  }, [initialEditingAssignment]);

  const performedActionName = useMemo(() => {
    if (isAssignmentScheduled) return `${t('Unassigned')}: `;

    return initialEditingAssignment?.archived
      ? `${t('Reassigned')}: `
      : `${t('Unassigned')}: `;
  }, [isAssignmentScheduled, initialEditingAssignment]);

  const handleNotificationPopupClose = useCallback(() => {
    dispatch(setIsAssignmentActionNotificationActive(false));
  }, [dispatch]);

  useEffect(() => {
    const hasAssignmentPanelBeenClosed =
      !!prevIsAssignmentPanelOpened && !isAssignmentPanelOpened;

    if (
      !editingAssignment ||
      isAssignmentPanelOpened ||
      hasAssignmentPanelBeenClosed
    )
      return;

    setInitialEditingAssignment(editingAssignment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAssignment, isAssignmentPanelOpened]);

  // hide notification popup on assignment panel opening
  useEffect(() => {
    if (!isAssignmentPanelOpened) return;

    dispatch(setIsAssignmentActionNotificationActive(false));
  }, [isAssignmentPanelOpened, dispatch]);

  useEffect(() => {
    if (!isAssignmentActionNotificationActive) {
      return;
    }
    clearTimeout(window.popupCloseTimeout);
    window.popupCloseTimeout = setTimeout(handleNotificationPopupClose, 10000);
    buttonRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditingAssignment, isAssignmentScheduled]);

  const undoArchiving = (assignment: Assignment) => {
    const assignmentPayload = convertAssignmentIntoAssignmentPayload(
      assignment,
      { archived: assignment.archived },
    );

    editAssignment(assignmentPayload)
      .unwrap()
      .then(() => {
        writeAssignmentActionTimestamp();

        RealtimeService.sendRealtimeEvent(
          assignment.classBks[0],
          ASSIGNMENT_UPDATED_EVENT,
          {
            assignment,
            startDate: assignment.startDate,
            dueDate: assignment.dueDate,
            isDeleted: Boolean(assignment.archived),
          },
        );
      });

    handleNotificationPopupClose();
  };

  const undoDeletion = (assignment: Assignment) => {
    const assignmentPayload =
      convertAssignmentIntoAssignmentPayload(assignment);

    createAssignment(assignmentPayload)
      .unwrap()
      .then(() => {
        writeAssignmentActionTimestamp();

        RealtimeService.sendRealtimeEvent(
          assignment.classBks[0],
          ASSIGNMENT_UPDATED_EVENT,
          {
            assignment,
            startDate: assignment.startDate,
            dueDate: assignment.dueDate,
            isDeleted: Boolean(assignment.archived),
          },
        );
      });

    handleNotificationPopupClose();
  };

  const handleUndoActionXApiEvents = (
    assignment: Assignment,
    isRightClick = false,
  ) => {
    const eventType = performedActionName.includes(t('Unassigned'))
      ? XApiEventKeys.selectedUndoLessonUnassign
      : XApiEventKeys.selectedUndoLessonReassign;

    const resultExtensions = generateAssignmentActionsResultEventPayload({
      eventType,
      assignmentType: assignment.type,
      content: assignment.content,
      assignmentBusinessKeys: assignment.businessKey,
      assignmentClassBks: assignment.classBks,
      assignmentStartDate: assignment.startDate,
      assignmentDueDate: assignment.dueDate,
    });

    const assignmentClassBk = assignment.classBks[0];
    const assignmentClassGrade = classData?.[assignmentClassBk]?.grades ?? [];

    const contextExtensions = generateAssignmentActionsContextEventPayload({
      isRightClick,
      section: getAssignmentSection(assignment),
      url: window.location.href,
      page: getPage(),
      content:
        assignment.type === 'Cardstack'
          ? assignment.content[0]
          : assignment.content,
      classGrade: assignmentClassGrade,
      classBk: assignmentClassBk,
      lessonStatus: isScheduledAssignment(assignment)
        ? 'scheduled'
        : 'assigned',
      assignmentBusinessKey: assignment.businessKey,
    });

    eventService.send(eventType, resultExtensions, contextExtensions);
  };

  const handleUndoActionClickBtn = () => {
    if (!initialEditingAssignment) return;

    handleUndoActionXApiEvents(initialEditingAssignment);

    if (isAssignmentScheduled) {
      undoDeletion(initialEditingAssignment);
    } else {
      undoArchiving(initialEditingAssignment);
    }
  };

  const handleUndoBtnContextMenu = () => {
    if (!initialEditingAssignment) return;

    handleUndoActionXApiEvents(initialEditingAssignment, true);
  };

  const getAssignmentClassTitle = () => {
    const DEFAULT_TITLE = '';
    if (isFetching || !classes?.length || !initialEditingAssignment)
      return DEFAULT_TITLE;

    const { classBks } = initialEditingAssignment;
    const assigmentClassKey = classBks.length && classBks[0];

    if (!assigmentClassKey) return DEFAULT_TITLE;

    const assigmentClass = classes.find(({ classKey }) => {
      return classKey === assigmentClassKey;
    });

    return assigmentClass?.displayName ?? DEFAULT_TITLE;
  };

  return (
    <NotificationPopup
      isVisible={isAssignmentActionNotificationActive}
      handleClose={handleNotificationPopupClose}
      variant={notificationVariant}
    >
      <div className={styles.message}>
        {assignmentActionErrorMessage ? (
          <>{assignmentActionErrorMessage}</>
        ) : (
          <>
            <b>{performedActionName}</b>
            {getAssignmentClassTitle()}
          </>
        )}
      </div>
      {!assignmentActionErrorMessage && (
        <button
          className={styles.undoBtn}
          ref={buttonRef}
          onClick={handleUndoActionClickBtn}
          onContextMenu={handleUndoBtnContextMenu}
        >
          <RevertArrowIcon />
          <span>{t('Undo')}</span>
        </button>
      )}
    </NotificationPopup>
  );
};
